'use client';
import React, { useState } from 'react';
import { usePathname } from 'next/navigation';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import RequireAuth from '@/lib/auth/RequireAuth';
import { pageMeta } from '@/lib/navigation';

/**
 * Свёрнутость меню в localStorage.
 *
 * Макет общий и при переходах не размонтируется, так что состояния в React
 * хватило бы для навигации. Хранилище нужно ради перезагрузки страницы:
 * свернул один раз — свёрнуто всегда, а не до следующего F5.
 */
const COLLAPSED_KEY = 'callog.sidebar.collapsed';

function readCollapsed(): boolean {
  // На сервере localStorage нет; сайдбар всё равно не доходит до разметки
  // SSR — до него экран занят гардом авторизации.
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    // Приватный режим или запрет на хранилище — не повод падать.
    return false;
  }
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsed);
  const { title, breadcrumb } = pageMeta(usePathname());

  const toggleSidebar = () => {
    setSidebarCollapsed((collapsed) => {
      const next = !collapsed;
      try {
        window.localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // Не сохранилось — в этой сессии всё равно работает.
      }
      return next;
    });
  };

  return (
    <RequireAuth>
      <div className="flex h-screen bg-canvas overflow-hidden">
        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
        <div
          className="flex flex-col flex-1 overflow-hidden transition-all duration-300"
          style={{ marginLeft: sidebarCollapsed ? '64px' : '240px' }}
        >
          <Topbar pageTitle={title} breadcrumb={breadcrumb} />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="max-w-screen-2xl mx-auto">{children}</div>
          </main>
        </div>
      </div>
    </RequireAuth>
  );
}
